// Editar una jornada del multievento (pestaña Líneas y menús del formato
// nuevo): fecha, horas, llegada, lugar (el mismo selector de Google que el
// gestor heredado, con la ruta desde la sede), detalles de acceso y
// observaciones. Se guarda por el PUT de la jornada SOLO con lo que cambia
// (cuerpoActualizacionJornada); las personas se cambian en la cabecera de la
// jornada (PersonasJornada).
//
// Props: presupuesto, jornada, numero, onGuardar(cuerpo) -> Promise<boolean>, onCerrar
import React, { useState } from 'react';
import { placesAPI } from '../../api/api';
import DireccionAutocomplete from '../DireccionAutocomplete';
import ModalJornada, {
  claseBotonPrincipal, claseBotonSecundario, claseCampoJornada, claseEtiquetaJornada,
} from './ModalJornada';
import { cuerpoActualizacionJornada, textoJornada } from '../../utils/multieventoPantallas';

const datosIniciales = (j) => ({
  fecha: String(j?.fecha || '').slice(0, 10),
  hora_inicio: j?.hora_inicio || '',
  hora_fin: j?.hora_fin || '',
  hora_llegada_estimada: j?.hora_llegada_estimada || '',
  lugar_evento: j?.lugar_evento || '',
  direccion_exacta: j?.direccion_exacta || '',
  coordenadas: j?.coordenadas || null,
  place_id: j?.place_id || '',
  municipio: j?.municipio || '',
  distancia_km: j?.distancia_km ?? null,
  tiempo_viaje_min: j?.tiempo_viaje_min ?? null,
  coste_transporte: j?.coste_transporte ?? null,
  detalles_acceso: j?.detalles_acceso || '',
  observaciones: j?.observaciones || '',
  usarPrincipal: false,
});

// Lugar y ruta vacíos: la jornada usa la dirección principal
const SIN_LUGAR = {
  lugar_evento: '', direccion_exacta: '', coordenadas: null, place_id: '', municipio: '',
  distancia_km: null, tiempo_viaje_min: null, coste_transporte: null,
};

const EditarJornadaModal = ({ presupuesto, jornada, numero, onGuardar, onCerrar }) => {
  const [datos, setDatos] = useState(() => datosIniciales(jornada));
  const [calculando, setCalculando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const poner = (campo, valor) => setDatos(prev => ({ ...prev, [campo]: valor }));

  // Texto escrito a mano: lo de Google del lugar anterior ya no vale
  const escribirLugar = (texto) => setDatos(prev => ({
    ...prev,
    ...SIN_LUGAR,
    lugar_evento: texto,
    usarPrincipal: false,
  }));

  // Lugar elegido en Google: dirección, coordenadas y ruta desde la sede (la
  // misma petición que el gestor heredado, placesAPI.distancia)
  const seleccionarLugar = async (lugar) => {
    setDatos(prev => ({
      ...prev,
      ...SIN_LUGAR,
      lugar_evento: lugar.nombre || lugar.direccion || '',
      direccion_exacta: lugar.direccion || '',
      coordenadas: lugar.lat != null ? { lat: lugar.lat, lng: lugar.lng } : null,
      place_id: lugar.place_id || '',
      municipio: lugar.municipio || '',
      usarPrincipal: false,
    }));
    if (lugar.lat == null || lugar.lng == null) return;
    setCalculando(true);
    try {
      const { data } = await placesAPI.distancia(lugar.lat, lugar.lng);
      if (!data?.disponible) return;
      const km = Number(data.distancia_km) || 0;
      const minutos = parseInt(data.tiempo_min, 10) || 0;
      // Coste del viaje: el del servidor; si no viene, la fórmula del gestor
      // heredado (ida y vuelta: 0,10 EUR por km y 0,26 EUR por minuto)
      const coste = Number.isFinite(Number(data.coste_transporte)) && data.coste_transporte !== null
        ? Number(data.coste_transporte)
        : (km * 2 * 0.10) + (minutos * 2 * 0.26);
      setDatos(prev => ({
        ...prev,
        distancia_km: Number(km.toFixed(2)),
        tiempo_viaje_min: minutos,
        coste_transporte: Number(coste.toFixed(2)),
      }));
    } catch (e) {
      setAviso('No se ha podido calcular la ruta desde la sede: el lugar se guarda igual y la ruta queda pendiente.');
    } finally {
      setCalculando(false);
    }
  };

  const usarPrincipal = () => setDatos(prev => ({ ...prev, ...SIN_LUGAR, usarPrincipal: true }));

  const guardar = async () => {
    const cuerpo = cuerpoActualizacionJornada(jornada, datos);
    if (!cuerpo) {
      onCerrar();
      return;
    }
    setGuardando(true);
    const ok = await onGuardar(cuerpo);
    setGuardando(false);
    if (ok) onCerrar();
  };

  const hereda = datos.usarPrincipal || !String(datos.lugar_evento || '').trim();
  const fechaLarga = datos.fecha
    ? new Date(`${datos.fecha}T12:00:00`).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  return (
    <ModalJornada
      titulo="Editar jornada"
      subtitulo={textoJornada(jornada, numero)}
      onCerrar={onCerrar}
      testid={`modal-editar-jornada-${jornada?.id}`}
      pie={(
        <>
          <button type="button" onClick={onCerrar} className={claseBotonSecundario}>Cancelar</button>
          <button type="button" onClick={guardar} disabled={guardando || calculando} className={claseBotonPrincipal}
            data-testid="editar-jornada-guardar">
            {guardando ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </>
      )}
    >
      <label className={claseEtiquetaJornada}>
        Fecha
        <input type="date" value={datos.fecha} onChange={(e) => poner('fecha', e.target.value)}
          className={claseCampoJornada} data-testid="editar-jornada-fecha" />
        {fechaLarga && <span className="font-normal text-[#33443C]">{fechaLarga}</span>}
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className={claseEtiquetaJornada}>
          Hora de inicio
          <input type="time" value={datos.hora_inicio} onChange={(e) => poner('hora_inicio', e.target.value)}
            className={claseCampoJornada} data-testid="editar-jornada-hora-inicio" />
        </label>
        <label className={claseEtiquetaJornada}>
          Hora de fin
          <input type="time" value={datos.hora_fin} onChange={(e) => poner('hora_fin', e.target.value)}
            className={claseCampoJornada} data-testid="editar-jornada-hora-fin" />
        </label>
        <label className={claseEtiquetaJornada}>
          Llegada al lugar
          <input type="time" value={datos.hora_llegada_estimada} onChange={(e) => poner('hora_llegada_estimada', e.target.value)}
            className={claseCampoJornada} data-testid="editar-jornada-hora-llegada" />
        </label>
      </div>
      <p className="text-xs text-[#5B6B64]">
        Si cambia la hora de inicio, los servicios sin hora fija se mueven lo mismo.
      </p>

      <div className="flex flex-col gap-1">
        <label htmlFor={`editar-jornada-lugar-${jornada?.id}`} className="text-xs font-semibold text-[#5B6B64]">
          Lugar de la jornada
        </label>
        <DireccionAutocomplete
          id={`editar-jornada-lugar-${jornada?.id}`}
          value={datos.lugar_evento}
          onChange={escribirLugar}
          onSelect={seleccionarLugar}
          placeholder="Misma dirección principal"
          className={claseCampoJornada}
        />
        {datos.direccion_exacta && datos.direccion_exacta !== datos.lugar_evento && (
          <p className="text-xs text-[#33443C]">{datos.direccion_exacta}</p>
        )}
        {hereda ? (
          <p className="text-xs text-[#5B6B64]" data-testid="editar-jornada-hereda">
            Vacío: la jornada usa la dirección principal{presupuesto?.lugar_evento ? ` (${presupuesto.lugar_evento})` : ''} y la sigue si cambia. Escribe otra solo si esta jornada es en otro sitio.
          </p>
        ) : (
          <button type="button" onClick={usarPrincipal}
            className="self-start text-xs font-semibold text-duetto-primary hover:underline"
            data-testid="editar-jornada-usar-principal">
            Usar la dirección principal
          </button>
        )}
        {calculando && <p className="text-xs text-[#5B6B64]">Calculando la ruta desde la sede...</p>}
        {!calculando && !hereda && datos.distancia_km != null && (
          <p className="text-xs text-[#0F5E43]">
            {Number(datos.distancia_km).toFixed(2)} km (ida) · {datos.tiempo_viaje_min} min · transporte estimado {Number(datos.coste_transporte || 0).toFixed(2)} EUR
          </p>
        )}
        {aviso && <p className="text-xs text-[#7A3A06]">{aviso}</p>}
      </div>

      <label className={claseEtiquetaJornada}>
        Detalles de acceso (opcional)
        <input type="text" value={datos.detalles_acceso} onChange={(e) => poner('detalles_acceso', e.target.value)}
          placeholder="Planta, oficina, contacto interno, código de aparcamiento..."
          className={claseCampoJornada} data-testid="editar-jornada-acceso" />
      </label>
      <label className={claseEtiquetaJornada}>
        Observaciones para cocina
        <textarea rows={3} value={datos.observaciones} onChange={(e) => poner('observaciones', e.target.value)}
          placeholder="Instrucciones especiales, alergias, requisitos de montaje..."
          className="w-full px-3 py-2 text-sm border border-[#CBD3CF] rounded-lg text-[#16221D] focus:outline-none focus:ring-2 focus:ring-duetto-primary resize-none"
          data-testid="editar-jornada-observaciones" />
        <span className="font-normal">Salen en la orden de cocina de esta jornada.</span>
      </label>
    </ModalJornada>
  );
};

export default EditarJornadaModal;
